import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useManagedDeviceTypeKinds } from '../../../hooks/useManagedDeviceTypes';
import { useCreateManagedDevice } from '../../../hooks/useManagedDevices';
import { vpnNetworksApi } from '../../../services/vpnNetworksApi';
import { getTenantId } from '../../../config/tenant';
import { isHostsType, normalizeTypeSlug, HOSTS_TYPE_SLUG } from '../typeSlugs';
import clsx from 'clsx';

/**
 * Modal for creating new managed devices with type-aware forms
 */
const CreateDeviceModal = ({ show, onHide, onDeviceCreated, selectedType }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    kind: '',
    description: '',
    vpnNetworkId: '',
    // SSH/VM specific
    sshPort: 22,
    remoteDisplayPort: '',
    remoteDisplayEnabled: false,
    installationCode: '',
  });

  const [errors, setErrors] = useState({});

  // Fetch kinds for the selected type
  const {
    data: kinds,
    isLoading: kindsLoading
  } = useManagedDeviceTypeKinds(selectedType?.slug);

  // Carrega VPN networks do tenant para o dropdown (React Query — cacheado).
  const tenantId = getTenantId();
  const {
    data: vpnNetworks = [],
    isLoading: vpnNetworksLoading,
  } = useQuery({
    queryKey: ['vpnNetworks', tenantId],
    queryFn: () => vpnNetworksApi.getByTenant(tenantId),
    enabled: !!tenantId && show,
    staleTime: 60_000,
  });

  // Create device mutation
  const createDevice = useCreateManagedDevice();

  // Reset form when modal opens/closes or type changes
  useEffect(() => {
    if (show && kinds && kinds.length > 0) {
      setFormData(prev => ({
        ...prev,
        kind: kinds[0].slug
      }));
    }
  }, [show, kinds]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('managedDevices.create.nameRequired');
    }

    if (!formData.kind) {
      newErrors.kind = t('managedDevices.create.kindRequired');
    }

    // Type-specific validation
    if (isHostsType(selectedType)) {
      if (formData.sshPort && (formData.sshPort < 1 || formData.sshPort > 65535)) {
        newErrors.sshPort = t('managedDevices.create.sshPortRange');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // VPN IP é atribuído automaticamente pelo backend (auto-alocação no VpnNetwork).
      // Credenciais de API do router (ApiUrl/ApiUsername/ApiPasswordTemporaria) são preenchidas
      // pelo fluxo de Bootstrap (MikrotikBootstrapCapabilityHandler) — não vêm do formulário.
      const deviceData = {
        name: formData.name.trim(),
        kind: formData.kind,
        description: formData.description.trim() || null,
        vpnNetworkId: formData.vpnNetworkId || null,
      };

      if (isHostsType(selectedType)) {
        deviceData.sshPort = formData.sshPort ? parseInt(formData.sshPort) : null;
        deviceData.remoteDisplayPort = formData.remoteDisplayPort ? parseInt(formData.remoteDisplayPort) : null;
        deviceData.remoteDisplayEnabled = formData.remoteDisplayEnabled;
        deviceData.installationCode = formData.installationCode.trim() || null;
      }

      await createDevice.mutateAsync(deviceData);
      onDeviceCreated();
      handleClose();
    } catch (error) {
      console.error('Failed to create device:', error);
      setErrors({ submit: error.message || t('managedDevices.create.submitFailed') });
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFormData({
      name: '',
      kind: '',
      description: '',
      vpnNetworkId: '',
      sshPort: 22,
      remoteDisplayPort: '',
      remoteDisplayEnabled: false,
      installationCode: '',
    });
    setErrors({});
    onHide();
  };

  // Render type-specific fields
  const renderTypeSpecificFields = () => {
    if (!selectedType) return null;

    switch (normalizeTypeSlug(selectedType.slug)) {
      case HOSTS_TYPE_SLUG:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('managedDevices.create.sshPort')}
                </label>
                <input
                  type="number"
                  name="sshPort"
                  value={formData.sshPort}
                  onChange={handleChange}
                  min="1"
                  max="65535"
                  className={clsx(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary",
                    errors.sshPort ? "border-red-300 bg-red-50" : "border-gray-300"
                  )}
                />
                {errors.sshPort && (
                  <p className="mt-1 text-sm text-red-600">{errors.sshPort}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('managedDevices.create.remoteDisplayPort')}
                </label>
                <input
                  type="number"
                  name="remoteDisplayPort"
                  value={formData.remoteDisplayPort}
                  onChange={handleChange}
                  min="1"
                  max="65535"
                  placeholder={t('managedDevices.create.optional')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="remoteDisplayEnabled"
                name="remoteDisplayEnabled"
                checked={formData.remoteDisplayEnabled}
                onChange={handleChange}
                className="h-4 w-4 text-theme-primary focus:ring-theme-primary border-gray-300 rounded"
              />
              <label htmlFor="remoteDisplayEnabled" className="ml-2 text-sm text-gray-700">
                {t('managedDevices.create.enableRemoteDisplay')}
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('managedDevices.create.installationCode')}
              </label>
              <input
                type="text"
                name="installationCode"
                value={formData.installationCode}
                onChange={handleChange}
                placeholder={t('managedDevices.create.installationCodePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary"
              />
            </div>
          </div>
        );

      case 'router':
        // ApiUrl/ApiUsername/ApiPasswordTemporaria são preenchidos pelo fluxo de Bootstrap
        // (MikrotikBootstrapCapabilityHandler gera usuário + senha no roteador). Não pedir ao usuário.
        return null;

      default:
        return null;
    }
  };

  if (!show) return null;

  const typeLabel = selectedType?.name || '';
  const modalTitle = typeLabel
    ? t('managedDevices.create.addNew', { type: typeLabel })
    : t('managedDevices.create.addNewFallback');
  const submitLabel = typeLabel
    ? t('managedDevices.create.createDevice', { type: typeLabel })
    : t('managedDevices.create.createFallback');
  const namePlaceholder = typeLabel
    ? t('managedDevices.create.devicePlaceholder', { type: typeLabel })
    : t('managedDevices.create.namePlaceholder');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {modalTitle}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition ease-in-out duration-150"
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {errors.submit && (
                <div className="flex items-center p-4 text-red-800 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>{errors.submit}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('managedDevices.create.nameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={namePlaceholder}
                    className={clsx(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary",
                      errors.name ? "border-red-300 bg-red-50" : "border-gray-300"
                    )}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('managedDevices.create.kind')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="kind"
                    value={formData.kind}
                    onChange={handleChange}
                    disabled={kindsLoading || !kinds}
                    className={clsx(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary",
                      errors.kind ? "border-red-300 bg-red-50" : "border-gray-300",
                      (kindsLoading || !kinds) && "bg-gray-100 cursor-not-allowed"
                    )}
                  >
                    <option value="">{t('managedDevices.create.selectKind')}</option>
                    {kinds?.map(kind => (
                      <option key={kind.id} value={kind.slug}>
                        {kind.name}
                      </option>
                    ))}
                  </select>
                  {errors.kind && (
                    <p className="mt-1 text-sm text-red-600">{errors.kind}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('managedDevices.create.descriptionLabel')}
                </label>
                <textarea
                  rows={2}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t('managedDevices.create.descriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('managedDevices.create.vpnNetwork')}
                </label>
                <select
                  name="vpnNetworkId"
                  value={formData.vpnNetworkId}
                  onChange={handleChange}
                  disabled={vpnNetworksLoading}
                  className={clsx(
                    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary",
                    vpnNetworksLoading && "bg-gray-100 cursor-not-allowed"
                  )}
                >
                  <option value="">{t('managedDevices.create.selectVpnNetwork')}</option>
                  {vpnNetworks.map((net) => (
                    <option key={net.id} value={net.id}>
                      {net.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {t(
                    'managedDevices.create.vpnIpAutoHint',
                    'O IP interno na VPN é atribuído automaticamente ao criar o dispositivo.',
                  )}
                </p>
              </div>

              {renderTypeSpecificFields()}
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                {t('managedDevices.create.cancel')}
              </button>
              <button
                type="submit"
                disabled={createDevice.isPending}
                className={clsx(
                  "px-4 py-2 text-white bg-theme-primary border border-theme-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary transition-colors",
                  createDevice.isPending
                    ? "bg-theme-primary/60 cursor-not-allowed"
                    : "hover:bg-theme-primary/90"
                )}
              >
                {createDevice.isPending ? t('managedDevices.create.creating') : submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDeviceModal;
